import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../config/axios";
import { useRole } from "../context/RoleContext";
import ApsReviewModal from "./ApsReviewModal";

function statusTone(status) {
  const value = String(status || "").toLowerCase();
  if (value === "complete" || value === "high") {
    return "bg-emerald-100 text-emerald-800";
  }
  if (value === "incomplete" || value === "low" || value === "medium") {
    return "bg-amber-100 text-amber-800";
  }
  return "bg-rose-100 text-rose-800";
}

function formatWhen(value) {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString();
  } catch (_) {
    return "";
  }
}

function emailStatusLabel(row) {
  const emails = (row.agentEmailedTo || []).filter(Boolean);
  const status = String(row.agentEmailStatus || "").toLowerCase();
  if (status === "sent" || row.emailedToAgent) {
    return {
      badge: "Sent to agent",
      tone: "bg-blue-100 text-blue-800",
      detail: emails.length
        ? `Review was sent to ${emails.join(", ")}${
            row.agentEmailedAt ? ` · ${formatWhen(row.agentEmailedAt)}` : ""
          }.`
        : `Review was sent to the agent${
            row.agentEmailedAt ? ` · ${formatWhen(row.agentEmailedAt)}` : ""
          }.`,
    };
  }
  if (status === "failed") {
    return {
      badge: "Email pending",
      tone: "bg-amber-100 text-amber-800",
      detail:
        row.agentEmailError ||
        "Email could not be sent yet. It will retry automatically.",
    };
  }
  if (status === "no_recipient") {
    return {
      badge: "Email pending",
      tone: "bg-amber-100 text-amber-800",
      detail:
        "Looking up the agent email and sending automatically…",
    };
  }
  if (row.hasReport) {
    return {
      badge: "Email pending",
      tone: "bg-amber-100 text-amber-800",
      detail: "Report is ready. Email to the agent will send automatically.",
    };
  }
  return null;
}

const TradeApsSummarySection = () => {
  const { isAdmin, isSuperUser, isBroker } = useRole();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openTrade, setOpenTrade] = useState(null);
  const [generatingId, setGeneratingId] = useState("");
  const knownEmailedRef = useRef(null);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await axiosInstance.get("/trade-documents/aps-reviews");
      const next = res.data.reviews || [];
      const justSent = Array.isArray(res.data.justSent) ? res.data.justSent : [];
      justSent.forEach((item) => {
        const emails = (item.recipients || []).filter(Boolean);
        toast.success(
          emails.length
            ? `Compliance review was sent to ${emails.join(", ")}.`
            : `Compliance review was sent to the agent for Trade #${
                item.tradeNumber || ""
              }.`,
          { autoClose: 10000 },
        );
      });
      if (knownEmailedRef.current) {
        const known = knownEmailedRef.current;
        const justSentIds = new Set(justSent.map((item) => String(item.tradeId)));
        next.forEach((row) => {
          const key = `${row.tradeId}:${row.agentEmailedAt || ""}`;
          if (
            row.emailedToAgent &&
            row.agentEmailedAt &&
            !known.has(key) &&
            !justSentIds.has(String(row.tradeId))
          ) {
            const emails = (row.agentEmailedTo || []).filter(Boolean);
            toast.success(
              emails.length
                ? `Compliance review was sent to ${emails.join(", ")}.`
                : "Compliance review was sent to the agent.",
              { autoClose: 10000 },
            );
          }
        });
      }
      knownEmailedRef.current = new Set(
        next
          .filter((row) => row.emailedToAgent && row.agentEmailedAt)
          .map((row) => `${row.tradeId}:${row.agentEmailedAt}`),
      );
      setRows(next);
    } catch (_) {
      if (!silent) setRows([]);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const timer = window.setInterval(() => load(true), 8000);
    return () => window.clearInterval(timer);
  }, []);

  if (!isAdmin && !isSuperUser && !isBroker) {
    return (
      <p className="text-gray-500">
        APS summaries are available after a Super User reviews the trade.
      </p>
    );
  }

  const openReport = (row) => {
    setOpenTrade({ tradeId: row.tradeId, tradeNumber: row.tradeNumber });
  };

  const generate = async (row) => {
    setGeneratingId(row.tradeId);
    try {
      await axiosInstance.post(
        `/trade-documents/${row.tradeId}/aps-review`,
        {},
        { timeout: 180000 },
      );
      await load();
      openReport(row);
    } catch (_) {
      await load();
    } finally {
      setGeneratingId("");
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">Compliance Review</h2>
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-900"></div>
        </div>
      ) : rows.length === 0 ? (
        <p className="text-gray-500">No reviewed trades yet.</p>
      ) : (
        <div className="space-y-4">
          {rows.map((row) => {
            const hasReport = Boolean(row.hasReport);
            const queued = Boolean(row.queuedAt) && !hasReport;
            const mail = emailStatusLabel(row);
            return (
              <div
                key={row.tradeId}
                className="bg-white rounded-lg shadow p-5 border border-gray-100"
              >
                <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Trade #{row.tradeNumber}
                    </h3>
                    <p className="text-gray-600">{row.address || "No address"}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {mail ? (
                      <span
                        className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${mail.tone}`}
                      >
                        {mail.badge}
                      </span>
                    ) : null}
                    {row.overallStatus ? (
                      <span
                        className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${statusTone(
                          row.overallStatus,
                        )}`}
                      >
                        {row.overallStatus.replace(/_/g, " ")}
                      </span>
                    ) : queued ? (
                      <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">
                        Generating…
                      </span>
                    ) : (
                      <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                        No report yet
                      </span>
                    )}
                  </div>
                </div>
                {hasReport && row.summary ? (
                  <p className="text-sm text-gray-800 leading-relaxed mb-3">
                    {row.summary}
                  </p>
                ) : queued ? (
                  <p className="text-sm text-gray-500 mb-3">
                    Automated compliance review is running. The agent will be
                    emailed automatically when it finishes.
                  </p>
                ) : (
                  <p className="text-sm text-gray-500 mb-3">
                    This trade is reviewed, but the compliance review has not
                    been generated yet.
                  </p>
                )}
                {mail?.detail ? (
                  <p className="text-xs text-blue-900 mb-3">{mail.detail}</p>
                ) : null}
                <button
                  type="button"
                  className="px-3 py-1.5 text-sm rounded-md bg-blue-900 text-white hover:bg-blue-800 disabled:bg-gray-400"
                  disabled={generatingId === row.tradeId || queued}
                  onClick={() =>
                    hasReport ? openReport(row) : generate(row)
                  }
                >
                  {generatingId === row.tradeId || queued
                    ? "Preparing..."
                    : hasReport
                      ? "Open report"
                      : "Generate report"}
                </button>
              </div>
            );
          })}
        </div>
      )}
      {openTrade ? (
        <ApsReviewModal
          tradeId={openTrade.tradeId}
          tradeNumber={openTrade.tradeNumber}
          onClose={() => setOpenTrade(null)}
        />
      ) : null}
    </div>
  );
};

export default TradeApsSummarySection;
